/**
 * First-Time User Onboarding Wizard
 * 4-step flow: Welcome+Legal -> Role -> Context -> Ready
 * Follows OpenEvidenceConnectDialog pattern
 */

import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import {
  Shield,
  Stethoscope,
  Users,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Building2,
} from 'lucide-react'
import { BAAContent, ToSContent } from '../legal'
import api from '../../services/api'
import { useSubscription } from '../../contexts/SubscriptionContext'

interface OnboardingWizardProps {
  open: boolean
  onComplete: () => void
}

type Step = 'welcome' | 'role' | 'context' | 'ready'

const SPECIALTIES = [
  'Internal Medicine',
  'Family Medicine',
  'Emergency Medicine',
  'Pediatrics',
  'Surgery',
  'Psychiatry',
  'OB/GYN',
  'Cardiology',
  'Neurology',
  'Oncology',
  'Other',
]

const EHR_SYSTEMS = [
  'Epic',
  'Cerner',
  'Meditech',
  'Allscripts',
  'athenahealth',
  'eClinicalWorks',
  'NextGen',
  'Other',
  'None',
]

export const OnboardingWizard: FC<OnboardingWizardProps> = ({
  open,
  onComplete,
}) => {
  const { refresh: refreshSubscription } = useSubscription()
  const [step, setStep] = useState<Step>('welcome')
  const [baaAccepted, setBaaAccepted] = useState(false)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [roleType, setRoleType] = useState<'doctor' | 'other' | null>(null)
  const [specialty, setSpecialty] = useState<string>('')
  const [otherRole, setOtherRole] = useState<string>('')
  const [ehrSystem, setEhrSystem] = useState<string>('')
  const [showBaa, setShowBaa] = useState(false)
  const [showTos, setShowTos] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const stepIndex = ['welcome', 'role', 'context', 'ready'].indexOf(step)

  const handleNext = () => {
    if (step === 'welcome' && baaAccepted && tosAccepted) {
      setStep('role')
    } else if (step === 'role' && roleType) {
      setStep('context')
    } else if (step === 'context') {
      setStep('ready')
    }
  }

  const handleBack = () => {
    if (step === 'role') setStep('welcome')
    else if (step === 'context') setStep('role')
    else if (step === 'ready') setStep('context')
  }

  const handleComplete = async () => {
    if (!roleType) return

    setIsSubmitting(true)
    setError(null)

    try {
      await api.post('/api/v1/onboarding/complete', {
        baa_accepted: true,
        tos_accepted: true,
        role_type: roleType,
        specialty: roleType === 'doctor' ? (specialty || null) : (otherRole || null),
        ehr_system: ehrSystem || null,
      })

      // Onboarding succeeded - close dialog immediately
      onComplete()

      // Refresh subscription in background (non-blocking)
      refreshSubscription().catch(() => {
        // Subscription refresh failed, but onboarding succeeded - ignore
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete onboarding')
      setIsSubmitting(false)
    }
  }

  const canProceed = () => {
    if (step === 'welcome') return baaAccepted && tosAccepted
    if (step === 'role') return roleType !== null
    return true
  }

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-lg p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border-border/50"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => step === 'welcome' && e.preventDefault()}
        >
          {/* Progress Indicator */}
          <div className="px-6 pt-5">
            <div className="flex gap-1.5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-all duration-300',
                    i <= stepIndex
                      ? 'bg-clinical-teal'
                      : 'bg-border/50'
                  )}
                />
              ))}
            </div>
          </div>

          {/* Header */}
          <DialogHeader className="px-6 py-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300',
                step === 'ready'
                  ? 'bg-clinical-sage/20'
                  : 'bg-gradient-to-br from-clinical-teal/20 to-clinical-teal/5 border border-clinical-teal/20'
              )}>
                {step === 'welcome' && <Shield className="h-6 w-6 text-clinical-teal" />}
                {step === 'role' && <Stethoscope className="h-6 w-6 text-clinical-teal" />}
                {step === 'context' && <Building2 className="h-6 w-6 text-clinical-teal" />}
                {step === 'ready' && <Sparkles className="h-6 w-6 text-clinical-sage" />}
              </div>
              <div>
                <DialogTitle className="text-lg font-display font-semibold">
                  {step === 'welcome' && 'Welcome to Casey'}
                  {step === 'role' && "What's your role?"}
                  {step === 'context' && 'Quick setup'}
                  {step === 'ready' && "You're all set!"}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {step === 'welcome' && 'Your clinical documentation assistant'}
                  {step === 'role' && 'Help us personalize your experience'}
                  {step === 'context' && 'Optional information'}
                  {step === 'ready' && 'Start using Casey'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-2">
            {/* Step 1: Welcome + Legal */}
            {step === 'welcome' && (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-muted-foreground">
                  Casey helps you create clinical documentation faster and more accurately.
                  Before we begin, please review and accept our agreements.
                </p>

                <div className="space-y-3">
                  {/* BAA Checkbox */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-surface/50 cursor-pointer hover:border-clinical-teal/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={baaAccepted}
                      onChange={(e) => setBaaAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border text-clinical-teal focus:ring-clinical-teal"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        I accept the{' '}
                        <button
                          onClick={(e) => { e.preventDefault(); setShowBaa(true) }}
                          className="text-clinical-teal hover:underline"
                        >
                          Business Associate Agreement
                        </button>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Required for HIPAA compliance
                      </p>
                    </div>
                  </label>

                  {/* ToS Checkbox */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-surface/50 cursor-pointer hover:border-clinical-teal/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={tosAccepted}
                      onChange={(e) => setTosAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border text-clinical-teal focus:ring-clinical-teal"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        I accept the{' '}
                        <button
                          onClick={(e) => { e.preventDefault(); setShowTos(true) }}
                          className="text-clinical-teal hover:underline"
                        >
                          Terms of Service
                        </button>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Our service agreement
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex items-center gap-2 p-3 rounded-lg bg-clinical-teal/5 border border-clinical-teal/20">
                  <Shield className="h-4 w-4 text-clinical-teal flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    Your data is protected under HIPAA with AES-256 encryption
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Role Selection */}
            {step === 'role' && (
              <div className="space-y-4 animate-fade-in">
                {/* Role Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => { setRoleType('doctor'); setOtherRole('') }}
                    className={cn(
                      'p-4 rounded-xl border-2 text-left transition-all duration-200',
                      roleType === 'doctor'
                        ? 'border-clinical-teal bg-clinical-teal/10'
                        : 'border-border/50 bg-surface/50 hover:border-clinical-teal/30'
                    )}
                  >
                    <div className="w-10 h-10 rounded-lg bg-clinical-teal/10 flex items-center justify-center mb-3">
                      <Stethoscope className="h-5 w-5 text-clinical-teal" />
                    </div>
                    <p className="text-sm font-medium">Physician</p>
                    <p className="text-xs text-muted-foreground">MD, DO, NP, PA</p>
                  </button>

                  <button
                    onClick={() => { setRoleType('other'); setSpecialty('') }}
                    className={cn(
                      'p-4 rounded-xl border-2 text-left transition-all duration-200',
                      roleType === 'other'
                        ? 'border-clinical-sage bg-clinical-sage/10'
                        : 'border-border/50 bg-surface/50 hover:border-clinical-sage/30'
                    )}
                  >
                    <div className="w-10 h-10 rounded-lg bg-clinical-sage/10 flex items-center justify-center mb-3">
                      <Users className="h-5 w-5 text-clinical-sage" />
                    </div>
                    <p className="text-sm font-medium">Other Staff</p>
                    <p className="text-xs text-muted-foreground">RN, MA, Admin</p>
                  </button>
                </div>

                {/* Specialty Dropdown (only for doctors) */}
                {roleType === 'doctor' && (
                  <div className="animate-fade-in">
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Specialty (optional)
                    </label>
                    <select
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-border/50 bg-surface text-sm focus:border-clinical-teal focus:ring-1 focus:ring-clinical-teal"
                    >
                      <option value="">Select specialty...</option>
                      {SPECIALTIES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Role Input (for other staff) */}
                {roleType === 'other' && (
                  <div className="animate-fade-in">
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      What's your role? (optional)
                    </label>
                    <input
                      type="text"
                      value={otherRole}
                      onChange={(e) => setOtherRole(e.target.value)}
                      placeholder="e.g., Nurse, Medical Assistant, Researcher..."
                      className="w-full h-10 px-3 rounded-lg border border-border/50 bg-surface text-sm focus:border-clinical-sage focus:ring-1 focus:ring-clinical-sage placeholder:text-muted-foreground/50"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Quick Context */}
            {step === 'context' && (
              <div className="space-y-4 animate-fade-in">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Which EHR do you use?
                  </label>
                  <select
                    value={ehrSystem}
                    onChange={(e) => setEhrSystem(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-border/50 bg-surface text-sm focus:border-clinical-teal focus:ring-1 focus:ring-clinical-teal"
                  >
                    <option value="">Select EHR system...</option>
                    {EHR_SYSTEMS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Helps Casey adapt to your workflow
                  </p>
                </div>

                <button
                  onClick={() => setStep('ready')}
                  className="text-xs text-muted-foreground hover:text-clinical-teal transition-colors"
                >
                  Skip this step -&gt;
                </button>
              </div>
            )}

            {/* Step 4: Ready */}
            {step === 'ready' && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-clinical-teal/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-clinical-teal" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Generate clinical notes</p>
                      <p className="text-xs text-muted-foreground">From conversations and dictation</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-clinical-teal/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-clinical-teal" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Search medical literature</p>
                      <p className="text-xs text-muted-foreground">PubMed, guidelines, drug info</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-clinical-teal/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-clinical-teal" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Optimize documentation</p>
                      <p className="text-xs text-muted-foreground">ICD-10 coding, billing support</p>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-clinical-coral/10 border border-clinical-coral/20">
                    <p className="text-xs text-clinical-coral">{error}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 py-4 border-t border-border/50 bg-surface/30">
            {step !== 'welcome' && step !== 'ready' && (
              <Button variant="ghost" onClick={handleBack} className="text-xs">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                Back
              </Button>
            )}

            {step === 'welcome' && (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="text-xs ml-auto"
              >
                Continue
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            )}

            {step === 'role' && (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="text-xs"
              >
                Continue
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            )}

            {step === 'context' && (
              <Button onClick={handleNext} className="text-xs">
                Continue
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            )}

            {step === 'ready' && (
              <Button
                onClick={handleComplete}
                disabled={isSubmitting}
                className="w-full text-sm bg-clinical-teal hover:bg-clinical-teal/90 shadow-glow"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Start Using Casey
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nested Dialogs for Legal Content */}
      <BAAContent open={showBaa} onOpenChange={setShowBaa} />
      <ToSContent open={showTos} onOpenChange={setShowTos} />
    </>
  )
}
